import React, { memo, useMemo, useState } from 'react'
import { Button, Input, Popover, Select } from 'antd'
import { Icon } from '@iconify/react'
import dayjs from 'dayjs'

import DateRangePicker from '../../../projects/components/DateRangePicker'
import ProjectMultiSelect from '../../../../components/ProjectMultiSelect'
import { REPORTING_DATE_DISPLAY_FORMAT } from '../../reporting.constants'
import { CAPACITY_STATUS, CAPACITY_STATUS_OPTIONS } from '../capacity.constants'
import { getDefaultCapacityRange } from '../capacity.utils'
import styles from '../ProjectCapacityDashboard.module.css'

const CapacityFilters = memo(
    ({
        search,
        projectIds = [],
        projects = [],
        statusFilter,
        start,
        end,
        onSearchChange,
        onProjectChange,
        onProjectSearch,
        onProjectPopupScroll,
        projectListLoading = false,
        projectListPage = 1,
        onProjectOpenChange,
        onStatusChange,
        onDateRangeChange,
        onClearFilters,
        onExport,
    }) => {
        const [filterPopoverOpen, setFilterPopoverOpen] = useState(false)

        const projectFilterCount = Array.isArray(projectIds)
            ? projectIds.length
            : 0
        const hasStatusFilter = statusFilter !== CAPACITY_STATUS.ALL
        const hasDateFilter = useMemo(() => {
            const defaults = getDefaultCapacityRange()
            return (
                !dayjs(start).isSame(dayjs(defaults.start), 'day') ||
                !dayjs(end).isSame(dayjs(defaults.end), 'day')
            )
        }, [start, end])

        const activeFilterCount =
            projectFilterCount +
            (hasStatusFilter ? 1 : 0) +
            (hasDateFilter ? 1 : 0)
        const hasActiveFilters = activeFilterCount > 0

        const rangeValue = useMemo(
            () => ({
                start,
                end,
            }),
            [start, end]
        )

        const filterPopoverContent = useMemo(
            () => (
                <div className={styles.filterPopoverPanel}>
                    <div className={styles.filterPopoverHeader}>
                        <span className={styles.filterPopoverTitle}>
                            Filters
                        </span>
                        <button
                            type="button"
                            className={`${styles.filterPopoverClear} ${
                                !hasActiveFilters
                                    ? styles.filterPopoverClearDisabled
                                    : ''
                            }`}
                            disabled={!hasActiveFilters}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                if (!hasActiveFilters) return
                                onClearFilters?.()
                                setFilterPopoverOpen(true)
                            }}
                        >
                            Clear
                        </button>
                    </div>

                    <div className={styles.filterPopoverSection}>
                        <div className={styles.filterPopoverLabel}>
                            Date range
                        </div>
                        <div className={styles.filterPopoverDateControls}>
                            <div className={styles.dateFilterWrap}>
                                <DateRangePicker
                                    value={rangeValue}
                                    onChange={onDateRangeChange}
                                    placeholder="Date range"
                                    parentComponent="taskDetailView"
                                    dateFormat={REPORTING_DATE_DISPLAY_FORMAT}
                                    showClear={false}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.filterPopoverSection}>
                        <div className={styles.filterPopoverLabel}>
                            Filter by Projects
                        </div>
                        <ProjectMultiSelect
                            wrapClassName={styles.filterPopoverProjectSelect}
                            projects={projects}
                            value={projectIds}
                            onChange={(ids) => onProjectChange?.(ids || [])}
                            placeholder="Select Projects"
                            showSearch
                            filterOption={false}
                            onSearch={onProjectSearch}
                            onPopupScroll={onProjectPopupScroll}
                            loading={projectListLoading}
                            page={projectListPage}
                            onOpenChange={onProjectOpenChange}
                        />
                    </div>

                    <div className={styles.filterPopoverSection}>
                        <div className={styles.filterPopoverLabel}>Status</div>
                        <Select
                            className={styles.filterPopoverStatusSelect}
                            value={statusFilter}
                            options={CAPACITY_STATUS_OPTIONS}
                            onChange={onStatusChange}
                            suffixIcon={
                                <Icon
                                    icon="mdi:chevron-down"
                                    width={18}
                                    height={18}
                                    className={styles.filterChevron}
                                />
                            }
                        />
                    </div>
                </div>
            ),
            [
                hasActiveFilters,
                onClearFilters,
                rangeValue,
                onDateRangeChange,
                projects,
                projectIds,
                onProjectChange,
                onProjectSearch,
                onProjectPopupScroll,
                projectListLoading,
                projectListPage,
                onProjectOpenChange,
                statusFilter,
                onStatusChange,
            ]
        )

        return (
            <div className={styles.headerControls}>
                <div className={styles.searchWrap}>
                    <Input
                        allowClear
                        placeholder="Search by name"
                        className={styles.search}
                        value={search}
                        onChange={(e) => onSearchChange?.(e.target.value)}
                        prefix={
                            <Icon icon="mdi:magnify" width={18} height={18} />
                        }
                    />
                </div>

                <div
                    className={
                        hasActiveFilters
                            ? styles.filterBadge
                            : styles.filterTrigger
                    }
                >
                    <Popover
                        arrow={false}
                        content={filterPopoverContent}
                        title={null}
                        trigger="click"
                        open={filterPopoverOpen}
                        onOpenChange={setFilterPopoverOpen}
                        placement="bottomRight"
                        overlayClassName={styles.filtersPopover}
                    >
                        {hasActiveFilters ? (
                            <div
                                className={styles.filterBadgeContent}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        setFilterPopoverOpen(true)
                                    }
                                }}
                            >
                                <Icon
                                    icon="mynaui:filter"
                                    width={16}
                                    height={16}
                                    className={styles.filterBadgeIcon}
                                />
                                <span className={styles.filterBadgeText}>
                                    Filters: {activeFilterCount}
                                </span>
                            </div>
                        ) : (
                            <Button
                                type="text"
                                className={styles.filterToolbarButton}
                                icon={
                                    <Icon
                                        icon="mynaui:filter"
                                        width={18}
                                        height={18}
                                    />
                                }
                            >
                                Filter
                            </Button>
                        )}
                    </Popover>
                    {hasActiveFilters ? (
                        <button
                            className={styles.filterBadgeClose}
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                onClearFilters?.()
                            }}
                            type="button"
                            aria-label="Clear filters"
                        >
                            <Icon
                                icon="mdi:close"
                                width={14}
                                height={14}
                                className={styles.filterBadgeCloseIcon}
                            />
                        </button>
                    ) : null}
                </div>

                <button
                    type="button"
                    className={styles.exportBtn}
                    onClick={onExport}
                    disabled
                    title="Export coming soon"
                >
                    <Icon icon="mdi:export-variant" width={16} height={16} />
                    Export
                </button>
            </div>
        )
    }
)

CapacityFilters.displayName = 'CapacityFilters'

export default CapacityFilters
